// The Flatland Trap: Ultimate Source Edition
// Full SugarCube Twee story for landing page

export const flatlandTrapTwee = `:: StoryTitle
The Flatland Trap: A Skeptic's Journey

:: Start
<<set $skepticism to 100>>
<<set $knowledge to 0>>
You sit at your desk, the glow of the monitor stinging your tired eyes. On the screen is a standard, "flat" website for a new marketing tool. 

It looks like every other site you've seen since 2015. A hero image of people high-fiving. Three columns of text. A "Contact Us" form that looks like a black hole.

"Interactive storytelling," the headline screams. "Boost conversions!"

You roll your eyes. //Great,// you think. //Another buzzword. Another gimmick to clutter up a perfectly good website.// You've been in this business for ten years. You know that people just want the facts, right?

Your finger hovers over the mouse.

[[Scroll down and skim the text (The Old Way)|The_Wall_of_Text]]
[[{{{pulse}}} Click the weird, pulsing button that says "Prove It" (The Adventure)|The_Portal]]

:: The_Wall_of_Text
You scroll. And scroll. 

There are paragraphs about "synergy" and "paradigm shifts." Your eyes glaze over. You check your watch. You've been here for 45 seconds, and you already feel the urge to check your email or leave the site entirely.

You yawn. This is "Passive Content." It demands nothing of you, so you give it nothing in return.

//This is boring,// you admit. //If I'm bored, my customers are bored.//

[[Go back and click the button|Start]]

:: The_Portal
<<set $skepticism to $skepticism - 10>>
You click the button. 

The screen doesn't just reload; it shifts. The text vanishes, replaced by a simple question addressed directly to //you//.

"You think this is a gimmick, don't you?"

A blinking cursor waits for your input. A chill runs down your spine. It's... talking to you?

[[Admit it: "Yeah, I think it's nonsense."|Admit_Skepticism]]
[[Play along: "Maybe. Show me the data."|Show_Data]]

:: Admit_Skepticism
<<set $skepticism to $skepticism - 10>>
You click: "Yeah, I think it's nonsense."

The screen responds instantly: "Good. Skepticism saves money. But let me ask you: You're still here, aren't you? You clicked. You're paying attention."

---

**DATA UPLOAD:** "**87% of content marketers agree** that interactive content grabs the attention of the reader more effectively than static content."

[[View Source|URL:https://contentmarketinginstitute.com/wp-content/uploads/2017/06/IonInteractive_Symphony_Final.pdf]]

---

You pause. You //did// click. And you're actually reading this, unlike that wall of text earlier.

[[Okay, you have my attention. Proceed.|The_Fork]]

:: Show_Data
<<set $knowledge to $knowledge + 1>>
You click: "Show me the data."

The system seems to purr. "I thought you'd ask that. You're a numbers person. You want to know if people actually stick around."

---

**DATA UPLOAD:** "The average time spent on pages featuring interactive elements is **4-5 minutes longer** than standard web pages."

[[View Source|URL:https://www.saleshub.ca/these-interactive-content-platforms-are-transforming-digital-marketing-results/]]

---

You check the time. 5 minutes? In internet time, that's an eternity.

[[Okay, keep going.|The_Fork]]

:: The_Fork
<<set $knowledge to $knowledge + 1>>
The narrative shifts. You are now standing at a fork in a digital road.

**To the Left:** A path paved with "Buy Now" buttons and generic flyers.

**To the Right:** A winding path full of questions, quizzes, and mysteries.

"This is the Engagement Gap," the text reads. "On the Left, you talk AT them. On the Right, you talk WITH them."

You feel a tug of curiosity toward the Right, but your business brain worries about the hard sell.

[[Take the Left Path (Traditional)|Dead_End]]
[[{{{pulse}}} Take the Right Path (Interactive)|The_Climb]]

:: Dead_End
You walk down the Left path. A salesman jumps out. "BUY THIS NOW!" he screams. You try to dodge him, but another popup appears. "SUBSCRIBE!"

You run away, annoyed. 

//This is what my customers feel like,// you realize. //Friction. Annoyance.//

[[Run back to the Fork|The_Fork]]

:: The_Climb
<<set $skepticism to $skepticism - 20>>
You take the winding path. Instead of screaming "BUY!", the path asks you: "What is your biggest struggle right now?"

You see two options floating in the air.

[[I need more leads and sales.|The_Conversion_Chamber]]
[[I need them to remember my brand.|The_Retention_Vault]]

:: The_Conversion_Chamber
<<set $knowledge to $knowledge + 1>>
You select "I need more leads."

The air shimmers. A treasure chest appears. It opens to reveal the hard performance numbers you demanded.

---

**DATA UPLOAD:** "Interactive calculators and quizzes see conversion rates averaging **40-50%**, compared to 10-20% for static content."

[[View Source|URL:https://outgrow.co/why-interactive/]]

---

"Why?" the text asks. "Because by the time they reach the 'Buy' button, they have already made choices. They are invested."

You nod slowly. The logic is sound. It's the 'Loop of Action.'

[[I see how this works now.|The_Summit]]

:: The_Retention_Vault
<<set $knowledge to $knowledge + 1>>
You select "I need them to remember me."

The view from here is clear. You realize that static text is easily forgotten, but an experience stays with you.

---

**DATA UPLOAD:** "**73% of marketers agree** that combining traditional content marketing tactics with interactive content enhances retention of the organization's message."

[[View Source|URL:https://contentmarketinginstitute.com/wp-content/uploads/2017/06/IonInteractive_Symphony_Final.pdf]]

---

"Nobody remembers a PDF," the voice says. "But everyone remembers the story they were part of."

[[I see how this works now.|The_Summit]]

:: The_Summit
You reach the top of the mountain. You look back at the "Flatland" where you started—the boring blocks of text, the generic images. It looks... dead.

You look at your own hands. You are engaged. You are educated. And frankly, you're ready to try this yourself.

**Your Journey Stats:**
!Skepticism Level: $skepticism%
!Knowledge Gained: $knowledge Verified Facts

"So," the final prompt asks. "Do you still think this is a gimmick? Or is it a better way to sell?"

[[It's a gimmick (Lie).|Final_Denial]]
[[{{{highlight}}} It's the future. Let's build one.|CTA_Link]]

:: Final_Denial
You try to click "It's a gimmick," but your hand won't move. You know too much now. The stats (40-50% conversions, 87% attention) are swirling in your head.

You sigh, defeated by logic.

[[Okay, fine. It works.|CTA_Link]]

:: CTA_Link
You smile. The skepticism is gone. You realize that if you can build a journey like this for your customers, you won't just be selling to them. You'll be taking them on an adventure.

The wall of text is dead. Long live the story.

---

**Ready to create your own interactive experience?**

[[Start Building|URL:/demo]]`;
